import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import { Box, Input, InputGroup, VStack, Text, Button, Image } from '@chakra-ui/react'
import { Icon } from '../icon'
import theme from '../../styles'
import { useTranslation } from 'react-i18next'
import { selectCurrentStep, useSelector } from '../../state'
import { cardImageMap, Steps } from '../../state/interfaces'

export interface InputDefaultProps {
  inputType: string
  name: string
  placeholder?: string
  onChange?: (values: string[]) => void
  grandImage?: string
}

export const InputDefault: FC<InputDefaultProps> = ({ placeholder, name, onChange, grandImage, inputType = null }) => {
  const { t } = useTranslation()
  const inputElement = useRef<HTMLInputElement>(null)
  const image = useMemo(() => (grandImage ? cardImageMap[grandImage] : ''), [grandImage])
  const [inputValue, setInputValue] = useState<string>('')
  const [checkBoxIsChecked, setCheckBoxIsChecked] = useState(false)
  const [needConsent, setNeedConsent] = useState(false)

  const VALID_EMAIL_REGEX = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i

  const isValidEmail = useCallback((rawEmail: string): boolean => {
    const matches = rawEmail.match(VALID_EMAIL_REGEX)

    return matches !== null && matches.length > 0
  }, [])

  const isValid = isValidEmail(inputValue)
  const invalidEmailMessage = t('error_invalid-email')

  const formatPhoneNumber = (inputValue: string) => {
    if (!inputValue) return inputValue

    const phoneNumber = inputValue.replace(/[^\d]/g, '')

    const phoneNumberLength = phoneNumber.length

    if (phoneNumberLength < 4) return phoneNumber

    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
    }

    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
  }

  const currentStep = useSelector(selectCurrentStep)
  const [inputInUse, setInputInUse] = useState(false)

  return (
    <VStack spacing={5} marginY={8} justifyContent={'left'} width={'full'}>
      <Box>
        <InputGroup
          minWidth={currentStep?.id === Steps.EMAIL_ADDRESS_LEAD ? { base: '100%', md: '600px' } : undefined}
          layerStyle={inputInUse ? theme.LayerStyles.INPUT_GROUP : ''}
        >
          <Input
            ref={inputElement}
            aria-label={placeholder}
            placeholder={placeholder}
            value={inputValue}
            variant="none"
            fontSize={{ base: 18, md: 22 }}
            layerStyle={theme.LayerStyles.INPUT}
            autoFocus
            name={name}
            backgroundColor="transparent"
            _placeholder={{ color: 'ashGrey' }}
            onClick={(e) => {
              e.stopPropagation()
              e.currentTarget.focus()
              setInputInUse(true)
            }}
            onBlur={() => setInputInUse(false)}
            onChange={
              inputType && inputType === 'phone'
                ? (event: React.ChangeEvent<HTMLInputElement>): void =>
                    setInputValue(formatPhoneNumber(event.target.value))
                : (event: React.ChangeEvent<HTMLInputElement>): void => {
                    setInputValue(event.target.value)
                    setInputInUse(true)
                  }
            }
            onKeyPress={(ev) => {
              if (
                ev.key === 'Enter' &&
                ((inputValue && inputType && inputType === 'email' && isValid) ||
                  (inputValue && inputType && inputType === 'phone' && inputValue.length === 14) ||
                  (inputValue && inputType === null))
              ) {
                ev.preventDefault()
                onChange && onChange([inputValue])
              }
            }}
            onKeyUp={() => {
              inputValue && inputValue.length >= 0 ? setNeedConsent(true) : setNeedConsent(false)
            }}
            onMouseEnter={() => {
              setInputInUse(true)
            }}
            onMouseLeave={() => {
              inputElement!.current!.value === '' ? setInputInUse(false) : setInputInUse(true)
            }}
          />
          {((inputValue && inputType && inputType === 'email' && isValid) ||
            (inputValue && inputType && inputType === 'newsletter-email' && isValid) ||
            (inputValue && inputType && inputType === 'phone' && inputValue.length === 14) ||
            (inputValue && inputType === null)) && (
            <Button
              role={'button'}
              position="absolute"
              borderRadius="md"
              variant="none"
              layerStyle={theme.LayerStyles.BTN_INPUT_PILL}
              onClick={() => {
                onChange && onChange([inputValue])
              }}
            >
              <Icon icon="arrow-right" color="white" />
            </Button>
          )}
        </InputGroup>
        {inputValue && inputType && (inputType === 'email' || inputType === 'mobile-search-email') && !isValid && (
          <Text color={'red'} fontSize="xs" padding={2}>
            {invalidEmailMessage}
          </Text>
        )}
      </Box>
      {inputType && inputType === 'mobile-search-email' && needConsent && (
        <label style={{ fontSize: '1rem' }}>
          <input
            onClick={() => setCheckBoxIsChecked(!checkBoxIsChecked)}
            type="checkbox"
            name="checkbox"
            defaultChecked={checkBoxIsChecked}
            style={{ marginRight: '0.6rem' }}
          />
          {t('common_email-communication-consent')}
        </label>
      )}
      {inputType && inputType === 'mobile-search-email' && (
        <Button
          borderRadius="xl"
          onClick={() => {
            onChange && onChange([inputValue])
          }}
          backgroundColor="green.200"
          fontSize={22}
          padding={6}
          color="white"
          fontWeight="bold"
          isDisabled={needConsent ? !isValid || !checkBoxIsChecked : false}
        >
          {t('common_show-results')}
        </Button>
      )}
      {image && <Image src={image} alt="image" width={['200px', '250px']} height={['200px', '250px']} />}
    </VStack>
  )
}
